import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Spinner,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import ConnectMetamaskButton from "../ConnectMetamaskButton";
import {
  mumbai,
  arbitrum,
  polygon,
  bsc,
} from "../ConnectMetamaskButton/networks";
import ModalEnvio from "./ModalEnvio";
import AwesomePagination from "../../../Components/Common/AwesomePagination";
import { APIClient } from "../../../helpers/api_helper";

const apiClient = new APIClient();

const EnviosSemanais = ({ isCliente }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [transation, setTransation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [network, setNetwork] = useState(
    process.env.REACT_APP_TESTNET === "true" ? mumbai : arbitrum
  );
  const [shouldFetch, setShouldFetch] = useState(true);
  const [coins, setCoins] = useState([]);

  // Estados para paginação
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);

  const user = JSON.parse(localStorage.getItem("authUser"));

  const fetchPrices = async () => {
    const response = await apiClient.get("/moedas/precos");
    if (response.sucesso) {
      setCoins(response.dados);
    } else {
      toast.error("Houve um erro ao buscar os preços das moedas!");
    }
  };

  const copyToClipboard = async (text) => {
    if (!navigator.clipboard) {
      toast.error("API de Clipboard não suportada no seu navegador.");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado para a área de transferência!");
    } catch (err) {
      toast.error("Falha ao copiar o texto");
    }
  };

  function splitDateTime(dateTimeStr) {
    const dateTime = parseISO(dateTimeStr);

    const data = format(dateTime, "dd MMM, yyyy", { locale: ptBR });
    const hora = format(dateTime, "hh:mm a");

    return {
      data,
      hora,
    };
  }

  const formatCurrency = (value) => {
    if (!value) {
      return "";
    }
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const fetchTransations = useCallback(async () => {
    setLoading(true);
    let response;

    if (isCliente) {
      response = await apiClient.post("/cripto-td/envio-semanal-cliente", {
        user_id: user.data._id,
        pagina: pagina,
        itens_pagina: 20,
      });
    } else {
      response = await apiClient.post("/cripto-td/envio-semanal", {
        pagina: pagina,
        itens_pagina: 20,
      });
    }

    if (response.sucesso) {
      const dados = response.dados;
      const filteredData = dados.data.filter(
        (item) => item.quantidade_moeda > 0
      );
      setTransation(filteredData);
      setTotalPaginas(dados.last_page);
      setTotalItens(dados.total);
    } else {
      toast.error(response.mensagem);
    }
    setLoading(false);
  }, [isCliente, user?.data?._id, pagina]);

  const handleButtonClick = useCallback((data) => {
    setModalData(data);
    setShowModal(true);
  }, []);

  useEffect(() => {
    if (shouldFetch) {
      const fetchData = async () => {
        await fetchPrices();
        await fetchTransations();
        setShouldFetch(false);
      };
      fetchData();
    }
  }, [shouldFetch, fetchTransations]);

  // Renderizar células para clientes
  const renderClienteTableRows = () => {
    return transation.map((item, idx) => (
      <tr key={item.id || idx}>
        {/* Data */}
        <td>
          {splitDateTime(item.data_envio).data}{" "}
          <small className="text-muted">
            {splitDateTime(item.data_envio).hora}
          </small>
        </td>

        {/* Conta */}
        <td>
          <b>{item.cripto_td_conta?.nome}</b>
          <br />
          <small className="text-muted">
            {`${item.carteira.substring(0, 6)}...${item.carteira.substring(
              item.carteira.length - 4
            )}`}
          </small>
        </td>

        {/* Moeda */}
        <td>
          <div className="d-flex align-items-center">
            <img
              src={`https://api.fort.exchange/crypto-icons/${
                item.moeda === "POL" ? "matic" : item.moeda.toLowerCase()
              }.svg`}
              alt=""
              className="avatar-xxs me-2"
            />
            {item.moeda}
          </div>
        </td>

        {/* Quantidade */}
        <td>
          {(() => {
            const moeda = coins.find((coin) => coin.moeda === item.moeda);
            const preco_atual_brl =
              item.quantidade_moeda * (moeda?.preco_atual_brl || 0);
            return (
              <>
                <h6
                  className="text-primary amount mb-1"
                  style={{ cursor: "pointer" }}
                  onClick={() => copyToClipboard(item.quantidade_moeda)}
                  title="Clique para copiar o valor"
                >
                  {parseFloat(item.quantidade_moeda).toFixed(
                    moeda?.decimais || 6
                  )}{" "}
                  {item.moeda}{" "}
                  <FeatherIcon
                    icon="copy"
                    style={{ color: "#ccc", height: "15px" }}
                  />
                </h6>
                <p className="text-success mb-0">
                  <b>Preço Atual: </b>
                  {formatCurrency(preco_atual_brl)}
                </p>
              </>
            );
          })()}
        </td>

        {/* Transaction ID */}
        <td>
          {(() => {
            const moeda = coins.find((coin) => coin.moeda === item.moeda);
            if (!item.transaction_id) return null;

            if (moeda?.rede === "Arbitrum") {
              return (
                <Link
                  to={
                    arbitrum.blockExplorerUrls[0] + "tx/" + item.transaction_id
                  }
                  className="btn btn-sm btn-secondary"
                  target="_blank"
                >
                  Transação
                </Link>
              );
            } else if (moeda?.rede === "Polygon") {
              return (
                <Link
                  to={
                    polygon.blockExplorerUrls[0] + "tx/" + item.transaction_id
                  }
                  className="btn btn-sm btn-secondary"
                  target="_blank"
                >
                  Transação
                </Link>
              );
            } else if (moeda?.rede === "BSC") {
              return (
                <Link
                  to={bsc.blockExplorerUrls[0] + "tx/" + item.transaction_id}
                  className="btn btn-sm btn-secondary"
                  target="_blank"
                >
                  Transação
                </Link>
              );
            } else if (moeda?.rede === "Core") {
              return (
                <Link
                  to={`https://blockchair.com/bitcoin/transaction/${item.transaction_id}`}
                  className="btn btn-sm btn-secondary"
                  target="_blank"
                >
                  Transação
                </Link>
              );
            } else {
              return (
                <Link
                  to={`https://solscan.io/tx/${item.transaction_id}`}
                  className="btn btn-sm btn-secondary"
                  target="_blank"
                >
                  Transação
                </Link>
              );
            }
          })()}
        </td>
      </tr>
    ));
  };

  // Renderizar células para admin
  const renderAdminTableRows = () => {
    return transation.map((item, idx) => (
      <tr key={item.id || idx}>
        {/* Moeda */}
        <td>
          <div className="d-flex align-items-center">
            <img
              src={`https://api.fort.exchange/crypto-icons/${
                item.moeda === "POL" ? "matic" : item.moeda.toLowerCase()
              }.svg`}
              alt=""
              className="avatar-xxs me-2"
            />
            {item.moeda}
          </div>
        </td>

        {/* Data */}
        <td>
          {splitDateTime(item.updated_at).data}{" "}
          <small className="text-muted">
            {splitDateTime(item.updated_at).hora}
          </small>
        </td>

        {/* Quantidade */}
        <td>
          <h6
            className="text-success amount mb-1"
            style={{ cursor: "pointer" }}
            onClick={() => copyToClipboard(item.quantidade_moeda)}
            title="Clique para copiar o valor"
          >
            {parseFloat(item.quantidade_moeda).toFixed(6)} {item.moeda}{" "}
            <FeatherIcon
              icon="copy"
              style={{ color: "#ccc", height: "15px" }}
            />
          </h6>
          <p className="text-muted mb-0">
            <b>Comprados a: </b>
            {formatCurrency(item.quantidade_brl)}
          </p>
          <p className="text-muted mb-0">
            <b>Preço Atual: </b>
            {formatCurrency(
              item.quantidade_moeda *
                (coins.find((coin) => coin.moeda === item.moeda)
                  ?.preco_atual_brl || 0)
            )}
          </p>
        </td>

        {/* Taxa Fort */}
        <td>{formatCurrency(item.lucro)}</td>

        {/* Taxa de Contrato */}
        <td>
          <h6 className="text-danger amount mb-1">
            {parseFloat(item.taxa_envio).toFixed(6)}
          </h6>
          <p className="text-muted mb-0">
            {formatCurrency(
              item.taxa_envio *
                (coins.find((coin) => coin.moeda === item.moeda)
                  ?.preco_atual_brl || 0)
            )}
          </p>
        </td>

        {/* Transaction ID */}
        <td>
          {(() => {
            const moeda = coins.find((coin) => coin.moeda === item.moeda);

            if (!item.transaction_id) {
              // Botão de envio quando não há transaction_id
              if (moeda?.rede === "Core" || moeda?.rede === "Solana") {
                return (
                  <Button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleButtonClick(item)}
                    disabled={network === null}
                  >
                    Enviar
                  </Button>
                );
              } else {
                return (
                  isConnected &&
                  (moeda?.rede !== network?.chainName ? (
                    <Button
                      className="btn btn-sm btn-warning"
                      onClick={() =>
                        setNetwork(
                          moeda.rede === "Arbitrum"
                            ? arbitrum
                            : moeda.rede === "Polygon"
                            ? polygon
                            : bsc
                        )
                      }
                    >
                      Mudar para {moeda.rede}
                    </Button>
                  ) : (
                    <Button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleButtonClick(item)}
                      disabled={network === null}
                    >
                      Enviar
                    </Button>
                  ))
                );
              }
            } else {
              if (moeda?.rede === "Arbitrum") {
                return (
                  <Link
                    to={
                      arbitrum.blockExplorerUrls[0] +
                      "tx/" +
                      item.transaction_id
                    }
                    className="btn btn-sm btn-secondary"
                    target="_blank"
                  >
                    Transação
                  </Link>
                );
              } else if (moeda?.rede === "Polygon") {
                return (
                  <Link
                    to={
                      polygon.blockExplorerUrls[0] + "tx/" + item.transaction_id
                    }
                    className="btn btn-sm btn-secondary"
                    target="_blank"
                  >
                    Transação
                  </Link>
                );
              } else if (moeda?.rede === "BSC") {
                return (
                  <Link
                    to={bsc.blockExplorerUrls[0] + "tx/" + item.transaction_id}
                    className="btn btn-sm btn-secondary"
                    target="_blank"
                  >
                    Transação
                  </Link>
                );
              } else if (moeda?.rede === "Core") {
                return (
                  <Link
                    to={`https://blockchair.com/bitcoin/transaction/${item.transaction_id}`}
                    className="btn btn-sm btn-secondary"
                    target="_blank"
                  >
                    Transação
                  </Link>
                );
              } else {
                return (
                  <Link
                    to={`https://solscan.io/tx/${item.transaction_id}`}
                    className="btn btn-sm btn-secondary"
                    target="_blank"
                  >
                    Transação
                  </Link>
                );
              }
            }
          })()}
        </td>
      </tr>
    ));
  };

  return (
    <React.Fragment>
      {!isCliente ? (
        <ModalEnvio
          show={showModal}
          data={modalData}
          setShowModal={setShowModal}
          network={network}
          setShouldFetch={setShouldFetch}
          coins={coins}
        />
      ) : null}

      <Card>
        <CardHeader>
          <Row className="align-items-center g-3">
            <Col md={3}>
              <h5 className="card-title mb-0">Últimos Envios Semanais</h5>
            </Col>
            {!isCliente ? (
              <div className="col-md-auto ms-auto">
                <div className="d-flex gap-2">
                  <ConnectMetamaskButton
                    networkConfig={network}
                    loading={loading}
                    isConnected={isConnected}
                    setIsConnected={setIsConnected}
                  />
                </div>
              </div>
            ) : null}
          </Row>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                padding: "20px",
              }}
            >
              <Spinner height={100} width={100} />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="align-middle table-nowrap mb-0 table">
                  <thead className="table-light">
                    <tr>
                      {isCliente ? (
                        <>
                          <th>Data</th>
                          <th>Conta</th>
                          <th>Moeda</th>
                          <th>Quantidade</th>
                          <th>Transaction ID</th>
                        </>
                      ) : (
                        <>
                          <th>Moeda</th>
                          <th>Data</th>
                          <th>Quantidade</th>
                          <th>Taxa Fort</th>
                          <th>Taxa de Contrato</th>
                          <th>Transaction ID</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {transation.length === 0 ? (
                      <tr>
                        <td
                          colSpan={isCliente ? 5 : 6}
                          className="text-center text-muted py-4"
                        >
                          Nenhum envio semanal encontrado.
                        </td>
                      </tr>
                    ) : isCliente ? (
                      renderClienteTableRows()
                    ) : (
                      renderAdminTableRows()
                    )}
                  </tbody>
                </table>
              </div>
              <AwesomePagination
                pagina={pagina}
                totalPaginas={totalPaginas}
                setPagina={(p) => {
                  setPagina(p);
                  setShouldFetch(true);
                }}
              />
            </>
          )}
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default EnviosSemanais;
